<script setup lang="ts">
import type { Component } from 'vue';
import { toTypedSchema } from '@vee-validate/zod';
import { z } from 'zod';

import MarketingFeatureList from '@/components/blocks/MarketingFeatureList.vue';
import Modal from '@/components/elements/Modal.vue';
import type { ShowLoginEvent } from '@/stores/event_bus';

import LaunchMarketing from './AuthForms/LaunchMarketing.vue';
import LoginForm from './AuthForms/LoginForm.vue';
import NostrEmailForm from './AuthForms/NostrEmailForm.vue';
import ResendVerification from './AuthForms/ResendVerification.vue';
import SignupForm from './AuthForms/SignupForm.vue';
import Unlock from './AuthForms/Unlock.vue';
import UnlockSecret from './AuthForms/UnlockSecret.vue';
import VerificationComplete from './AuthForms/VerificationComplete.vue';
import RecoveryKitDownload from './RecoveryKitDownload.vue';
import { ShowAuthForm } from './types';

const AuthForms: Record<ShowAuthForm, Component> = {
  [ShowAuthForm.Login]: LoginForm,
  [ShowAuthForm.Signup]: SignupForm,
  [ShowAuthForm.NostrEmail]: NostrEmailForm,
  [ShowAuthForm.RecoveryKitDownload]: RecoveryKitDownload,
  [ShowAuthForm.SecretKeyForm]: UnlockSecret,
  [ShowAuthForm.Locked]: Unlock,
  [ShowAuthForm.ResendVerification]: ResendVerification,
  [ShowAuthForm.VerificationComplete]: VerificationComplete,

  // not currently used anywhere
  // showResetPassword: ResetPasswordForm,
  // showLockedAccount: LockedAccountForm,
  // showLoginNewDevice: LoginNewDevice,
};

const props = withDefaults(
  defineProps<{
    activeOption?: ShowAuthForm | undefined;
    // allows for passing additional props to the auth form
    loginData?: ShowLoginEvent | undefined;
  }>(),
  {
    activeOption: ShowAuthForm.Login,
  },
);

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'updateTheme', theme?: string): void;
}>();

const activeOption = toRef(props, 'activeOption');
const changeForm: Ref<ShowAuthForm | undefined> = ref();
const showForm = computed(() => changeForm.value || activeOption.value);
const formOpts = computed(() => props.loginData?.opts || {});

const handleThemeUpdate = (theme) => {
  emit('updateTheme', theme || 'narrow');
};

const handleFormChange = (form: ShowAuthForm) => {
  changeForm.value = form;
};

const handleClose = () => {
  emit('close');
};

const validationSchema = toTypedSchema(
  z.object({
    email: z.string().email({ message: 'Enter a valid email address.' }),
    password: z.string().min(8, { message: 'Password must be at least 8 characters long.' }),
  }),
);

provide('validationSchema', validationSchema);

const cssClasses = computed(() => {
  return [
    showForm.value === ShowAuthForm.Locked && 'show-locked',
    showForm.value === ShowAuthForm.SecretKeyForm && 'show-secret',
  ];
});

const showMarketing = computed(() => {
  return (
    props.loginData?.form === ShowAuthForm.Signup && props.loginData?.opts?.showAppLaunchMarketing
  );
});

const app = computed(() => {
  return props.loginData?.form === ShowAuthForm.Signup ? props.loginData?.opts?.app : undefined;
});
</script>

<template>
  <Modal theme="transparent" size="narrow" has-icon-with-text @close="$emit('close')" max-on-mobile>
    <template #body>
      <div v-if="showMarketing && app" class="container authform">
        <div class="marketing-col">
          <LaunchMarketing :app="app" panel buttons @close="handleClose" />
          <MarketingFeatureList class="marketing-feature-list panel" hide-logo />
        </div>

        <component
          class="auth-col panel"
          @auth-form="handleFormChange"
          @close="handleClose"
          @update-theme="handleThemeUpdate"
          :is="AuthForms[showForm]"
          v-bind="formOpts"
          :class="[cssClasses]"
        />
      </div>

      <component
        v-else
        @auth-form="handleFormChange"
        @close="handleClose"
        @update-theme="handleThemeUpdate"
        :is="AuthForms[showForm]"
        v-bind="formOpts"
        :class="cssClasses"
      />
    </template>
  </Modal>
</template>

<style scoped>
.show-locked,
.show-secret {
  margin: auto;
}
.no-key-message {
  background: unset;
}

.form {
  max-width: 48%;
}

.panel {
  background: linear-gradient(180deg, rgb(65 59 79 / 50%) 0%, #413b4f 91.78%);
  border-radius: var(--module-border-radius);
  padding: 10px 24px 20px 24px;

  @supports (backdrop-filter: blur()) {
    backdrop-filter: blur(rem(18));
    background: linear-gradient(
      180deg,
      rgb(65 59 79 / 40%) 0%,
      transparentize(#413b4f, 0.4) 91.78%
    );
  }
}

.container {
  display: flex;
  flex-direction: row;
  gap: 2rem;
  padding: 0;
  &.authform {
    @media (max-width: 991px) {
      flex-direction: column;
    }
  }
  & > div {
    flex: 1;
  }
}

.marketing-col {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.row-panels {
  column-gap: 1rem;
}
</style>
